// Admin CoursesPage — paginated course catalog with publish action.
import { useState } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import type { Course, PaginatedResult } from "@dm-lms/shared/types";

export default function CoursesPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useFetch<PaginatedResult<Course>>(`/api/courses?page=${page}&pageSize=20`);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  async function publish(courseId: string) {
    setPublishingId(courseId);
    setPublishError(null);
    const result = await postJson<null>(`/api/courses/${courseId}/publish`);
    setPublishingId(null);
    if (!result.ok) {
      setPublishError(result.error);
      return;
    }
    refetch();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Courses</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {publishError && <p className="text-red-600 text-sm mb-3">{publishError}</p>}

      {!isLoading && !error && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-dm-slate text-sm">No courses found.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Mode</th>
                    <th className="px-4 py-3 font-medium">Capacity</th>
                    <th className="px-4 py-3 font-medium">Price (KES)</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((course) => (
                    <tr key={course.id}>
                      <td className="px-4 py-3 text-dm-navy font-medium">{course.code}</td>
                      <td className="px-4 py-3">{course.title}</td>
                      <td className="px-4 py-3 capitalize">{course.mode}</td>
                      <td className="px-4 py-3">{course.capacity}</td>
                      <td className="px-4 py-3">{course.price.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <Badge tone={course.isPublished ? "green" : "gray"}>
                          {course.isPublished ? "Published" : "Draft"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {!course.isPublished && (
                          <button
                            type="button"
                            className="text-xs px-3 py-1.5 rounded-md bg-dm-blue text-white disabled:opacity-50"
                            disabled={publishingId === course.id}
                            onClick={() => publish(course.id)}
                          >
                            Publish
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">Page {data.page} · {data.total} total</span>
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
